import React, { useState } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Si ya hay sesion activa, redirigir al destino original o al dashboard
  if (!authLoading && user && user.role === 'admin') {
    const redirectTo = location.state?.from?.pathname || '/admin';
    return <Navigate to={redirectTo} replace />;
  }

  const validateForm = () => {
    const newErrors = {};
    if (!email.trim()) newErrors.email = 'El email es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Ingresa un email válido';
    if (!password.trim()) newErrors.password = 'La contraseña es requerida';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        toast.success('Bienvenido al panel de administración');
        navigate('/admin');
      } else {
        toast.error('No tienes permisos de administrador');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Credenciales inválidas';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-steel-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="/images/logo.png" 
            alt="NAYACEROS" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="font-heading text-2xl font-bold text-white uppercase tracking-wider">
            Panel de Administración
          </h1>
        </div>

        <div className="bg-white rounded shadow-xl p-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-accent-primary rounded-full flex items-center justify-center">
              <Lock className="text-white" size={32} />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form" noValidate>
            <div>
              <label className="block text-sm font-medium text-steel-dark mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors({...errors, email: ''}); }}
                className={`input-industrial ${errors.email ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                placeholder="admin@nayaceros.com"
                data-testid="login-email"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="error-login-email">{errors.email}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-steel-dark mb-1">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors({...errors, password: ''}); }}
                className={`input-industrial ${errors.password ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                placeholder="••••••••"
                data-testid="login-password"
              />
              {errors.password && <p className="text-red-500 text-xs mt-1" data-testid="error-login-password">{errors.password}</p>}
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : null}
              Iniciar Sesión
            </button>
          </form>
        </div>

        <p className="text-center text-text-light text-sm mt-6">
          © {new Date().getFullYear()} NAYACEROS - Panel de Administración
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
